import classNames from 'classnames';
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { field } from './styles';

export type ComboOption = { value: string; label: string; hint?: string; keywords?: string };

// Search ignores case and accents: "aland" finds "Åland Islands", "sao" finds "São Tomé".
const norm = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

/** Options matching every word typed: names that start with it first, then words that do, then the rest. */
const filterOptions = (options: ComboOption[], query: string) => {
  const q = norm(query);
  if (!q) return options;
  const words = q.split(/\s+/);
  const scored: { o: ComboOption; score: number }[] = [];
  for (const o of options) {
    const label = norm(o.label);
    const hay = `${label} ${norm(o.keywords ?? '')}`;
    if (!words.every((w) => hay.includes(w))) continue;
    const score = label.startsWith(q) ? 0 : label.split(/\s+/).some((w) => w.startsWith(words[0])) ? 1 : 2;
    scored.push({ o, score });
  }
  return scored.sort((a, b) => a.score - b.score).map((s) => s.o);
};

/**
 * A select you can type into to search (UI-1: easier than scrolling 249 countries on a
 * phone). With `onCreate`, what you type and is not an option yet can be created on
 * the spot, and is then picked. Keyboard: arrows, Enter, Escape. Nothing has a border:
 * the field is a control, the list floats above everything (elevation 4).
 */
export const Combobox = ({
  id,
  value,
  options,
  onChange,
  placeholder,
  onCreate,
  createLabel = (text) => `Create “${text}”`,
  clearLabel,
  disabled,
  loading,
}: {
  id: string;
  value: string;
  options: ComboOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  /** Creates an option from the text typed and returns it, or null when it could not. */
  onCreate?: (text: string) => Promise<ComboOption | null>;
  createLabel?: (text: string) => string;
  /** When set, an option on top that empties the value, such as "Unknown". */
  clearLabel?: string;
  disabled?: boolean;
  loading?: boolean;
}) => {
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [creating, setCreating] = useState(false);

  const selected = options.find((o) => o.value === value);
  const typed = query.trim();
  const canCreate = Boolean(onCreate) && typed !== '' && !options.some((o) => norm(o.label) === norm(typed));

  // What the list shows, in order: clear, the matches, then create. Typing highlights the
  // first match, so Enter picks it; it creates only when nothing matches.
  const items = useMemo(() => {
    const list: ({ kind: 'option'; o: ComboOption } | { kind: 'create' } | { kind: 'clear' })[] = [];
    if (clearLabel && !typed) list.push({ kind: 'clear' });
    for (const o of filterOptions(options, query)) list.push({ kind: 'option', o });
    if (canCreate) list.push({ kind: 'create' });
    return list;
  }, [options, query, canCreate, clearLabel, typed]);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    const at = items.findIndex((i) => i.kind === 'option' && i.o.value === value);
    setActive(Math.max(0, at));
    // On a phone the keyboard takes half the screen: keep the field and its list in view.
    window.setTimeout(() => wrapRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 50);
  };
  const close = () => {
    setOpen(false);
    setQuery('');
  };
  const pick = async (i: number) => {
    const item = items[i];
    if (!item) return;
    if (item.kind === 'option') onChange(item.o.value);
    if (item.kind === 'clear') onChange('');
    if (item.kind === 'create' && onCreate) {
      setCreating(true);
      try {
        const made = await onCreate(typed);
        if (made) onChange(made.value);
      } finally {
        setCreating(false);
      }
    }
    close();
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return openList();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((a) => (items.length ? (a + step + items.length) % items.length : 0));
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault(); // never submits the form while choosing
      void pick(active);
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation(); // closes the list, not the dialog around it
      close();
    }
  };

  const optionId = (i: number) => `${listId}-${i}`;

  return (
    <div
      ref={wrapRef}
      className="relative"
      onBlur={(e) => {
        if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) close();
      }}
    >
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && items[active] ? optionId(active) : undefined}
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        disabled={disabled}
        className={classNames(field.input, 'pr-10')}
        placeholder={loading ? 'Loading…' : open ? 'Type to search' : placeholder}
        value={open ? query : (selected?.label ?? '')}
        onFocus={openList}
        onClick={() => !open && openList()}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          if (!open) setOpen(true);
        }}
        onKeyDown={onKey}
      />
      <svg viewBox="0 0 16 16" className="pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-faint" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d={open ? 'M4 10l4-4 4 4' : 'M4 6l4 4 4-4'} />
      </svg>

      {open && (
        <ul id={listId} role="listbox" className="absolute inset-x-0 top-full z-30 mt-1.5 max-h-64 animate-drop-in overflow-y-auto rounded-lg bg-surface-raised p-1 shadow-e4">
          {items.length === 0 && <li className="px-3 py-2.5 text-sm text-fg-faint">{loading ? 'Loading…' : 'Nothing matches.'}</li>}
          {items.map((item, i) => {
            const isSelected = item.kind === 'option' ? item.o.value === value : item.kind === 'clear' ? value === '' : false;
            return (
              <li
                key={item.kind === 'option' ? item.o.value : item.kind}
                id={optionId(i)}
                role="option"
                aria-selected={isSelected}
                // Keep the focus in the field, so the blur does not close the list first.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void pick(i)}
                onMouseEnter={() => setActive(i)}
                className={classNames(
                  'flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-md px-3 text-base md:min-h-9 md:text-sm',
                  i === active ? 'bg-surface-hover text-fg-strong' : 'text-fg',
                )}
              >
                {item.kind === 'create' ? (
                  <span className="flex items-center gap-2 text-brand">
                    <span aria-hidden>+</span>
                    {creating ? 'Saving…' : createLabel(typed)}
                  </span>
                ) : item.kind === 'clear' ? (
                  <span className="text-fg-muted">{clearLabel}</span>
                ) : (
                  <span className="min-w-0 truncate">{item.o.label}</span>
                )}
                <span className="flex shrink-0 items-center gap-2 text-xs text-fg-faint">
                  {item.kind === 'option' && item.o.hint}
                  {isSelected && (
                    <svg viewBox="0 0 16 16" className="h-4 w-4 text-brand" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M3.5 8.5l3 3 6-7" />
                    </svg>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Combobox;
